<template>
  <div class="map-view">
    <MapToolbar
      v-model:selected-params="selectedParams"
      v-model:selected-month="selectedMonth"
      v-model:selected-year="selectedYear"
      v-model:selected-district="selectedDistrictKey"
      v-model:selected-region="selectedRegionKey"
      @update:selected-params="loadData"
      @update:selected-month="loadData"
      @update:selected-year="loadData"
      @update:selected-district="loadData"
      @update:selected-region="loadData"
    />

    <MainMap
      v-model:is-data-loading="isDataLoading"
      v-model:is-map-loading="isMapLoading"
      v-model:selected-district="selectedDistrictKey"
      v-model:selected-region="selectedRegionKey"
      :selected-params="selectedParams"
      @update:selected-district="loadData"
      @update:selected-region="loadData"
    />
    <MapLegend :param-name="selectedParams" />
    <MapAnalytics
      :selected-district="selectedDistrictKey"
      :selected-region="selectedRegionKey"
      :param-name="selectedParams"
      :selected-year="selectedYear"
      :selected-month="selectedMonth"
    />
  </div>
</template>

<script setup lang="ts">
import MainMap from "@/components/modules/MainMap/MainMap.vue";
import {useFilesStore} from "@/stores/files";
import {onMounted, ref} from "vue";
import MapToolbar from "@/components/modules/MainMap/MapToolbar.vue";
import {useStore} from "@/stores";
import {storeToRefs} from "pinia";
import type {MapDisplayParamsKey} from "@/composables/constants/params";
import {FederalDistrictDataQuery, FilesDataQuery} from "@/types/files";
import MapLegend from "@/components/modules/MainMap/MapLegend.vue";
import MapAnalytics from "@/components/modules/Analytics/MapAnalytics.vue";

const filesStore = useFilesStore();
const mainStore = useStore();

const {selectedDistrictKey, selectedRegionKey} = storeToRefs(filesStore);
const {forecastStatusDisplay} = storeToRefs(mainStore);

// Флаг загрузки данных для карты.
const isDataLoading = ref(false);

// Флаг загрузки карты.
const isMapLoading = ref(false);

// Активный выбранный параметр
const selectedParams = ref<MapDisplayParamsKey>("cases_accepted");

// Активный месяц и год
const { selectedMonth } = storeToRefs(filesStore);
const { selectedYear } = storeToRefs(filesStore);

onMounted(async (): Promise<void> => {
  await loadData();
});

// Функция загрузки данных файлов
const loadFilesDataOnly = async () => {
  const params: FilesDataQuery = {};

  // Выбранный месяц, год.
  if (selectedMonth.value !== "all") {
    params.month = selectedMonth.value;
  }

  params.year = selectedYear.value;

  // Фильтр по округу (только если не выбран конкретный регион)
  if (selectedDistrictKey.value && !selectedRegionKey.value) {
    params.federal_districts = selectedDistrictKey.value;
  }

  // Фильтр по региону (имеет приоритет над округом)
  if (selectedRegionKey.value) {
    params.regions = selectedRegionKey.value;
  }

  await filesStore.getFilesData(params);
};

// Функция загрузки данных по федеральным округам
const loadFederalDistrictDataOnly = async () => {
  const params: FederalDistrictDataQuery = {
    metricName: selectedParams.value,
    year: selectedYear.value,
  };

  // Выбранный месяц
  if (selectedMonth.value !== "all") {
    params.month = selectedMonth.value;
  }

  await filesStore.getFederalDistrictData(params);
};

// Функция загрузки прогноза
const loadForecast = async () => {
  if (!forecastStatusDisplay.value) {
    return;
  }

  try {
    const region = selectedRegionKey.value ? selectedRegionKey.value : undefined;
    const district = selectedDistrictKey.value ? selectedDistrictKey.value : undefined;

    await mainStore.getForecast(region, district);
  } catch (error) {
    console.error("Ошибка загрузки прогноза:", error);
    mainStore.showWarning("Ошибка", "Не удалось загрузить данные прогноза");
  }
};

// Общая функция загрузки всех данных
const loadData = async () => {
  try {
    isMapLoading.value = true;
    isDataLoading.value = true;

    await Promise.all([
      loadFilesDataOnly(),
      loadFederalDistrictDataOnly(),
      loadForecast()
    ]);
  } catch (error) {
    console.error("Ошибка загрузки данных:", error);
    mainStore.showWarning("Ошибка", "Не удалось загрузить данные за выбранный период");
  } finally {
    setTimeout(() => {
      console.log("set timeout");
      isMapLoading.value = false;
      isDataLoading.value = false;
    }, 1);
  }
};
</script>

<style lang="scss" scoped>
.map-view {
  position: relative;
  width: 100%;
  height: 100%;

  &__statistic {
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
  }
}
</style>
